import React, { useState } from 'react'
import { useDarkMode } from '../App'
import { BOOK_MANAGEMENT_API_BASE } from '../lib/api'

interface BookUploadData {
  title: string
  authors: string[]
  year: number
  license: string
  type: 'book' | 'paper'
  description: string
  slug?: string
  featured?: boolean
}

interface UploadedFile {
  file: File
  preview?: string
}

interface BookUploadFormProps {
  onSuccess?: (data: any) => void
  onError?: (error: string) => void
}

export default function BookUploadForm({ onSuccess, onError }: BookUploadFormProps) {
  const { isDark } = useDarkMode()
  const [isUploading, setIsUploading] = useState(false)
  const [bookFile, setBookFile] = useState<UploadedFile | null>(null)
  const [imageFile, setImageFile] = useState<UploadedFile | null>(null)
  const [formData, setFormData] = useState<BookUploadData>({
    title: '',
    authors: [''],
    year: new Date().getFullYear(),
    license: 'CC-BY',
    type: 'book',
    description: '',
    slug: '',
    featured: false
  })
  
  // Auto-generate slug from title
  const generateSlug = (title: string): string => {
    return title
      .toLowerCase()
      .replace(/[^\w\s\u0600-\u06FF-]/g, '') // Keep Arabic characters
      .replace(/\s+/g, '-')
      .trim() + '.pdf' // Add .pdf extension to match existing book format
  }

  const handleTitleChange = (title: string) => {
    setFormData(prev => ({
      ...prev,
      title,
      slug: prev.slug || generateSlug(title) // Only auto-generate if slug is empty
    }))
  }

  const handleAuthorsChange = (index: number, value: string) => {
    const newAuthors = [...formData.authors]
    newAuthors[index] = value
    setFormData(prev => ({ ...prev, authors: newAuthors }))
  }

  const addAuthor = () => {
    setFormData(prev => ({ ...prev, authors: [...prev.authors, ''] }))
  }

  const removeAuthor = (index: number) => {
    if (formData.authors.length > 1) {
      const newAuthors = formData.authors.filter((_, i) => i !== index)
      setFormData(prev => ({ ...prev, authors: newAuthors }))
    }
  }

  const handleFileChange = (type: 'book' | 'image', file: File | null) => {
    if (!file) return

    if (type === 'book') {
      if (file.type !== 'application/pdf') {
        onError?.('Only PDF files are allowed for books')
        return
      }
      setBookFile({ file })
    } else {
      const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp']
      if (!allowedTypes.includes(file.type)) {
        onError?.('Only PNG, JPG, JPEG, and WebP files are allowed for images')
        return
      }
      
      // Create preview for image
      const reader = new FileReader()
      reader.onload = (e) => {
        setImageFile({ file, preview: e.target?.result as string })
      }
      reader.readAsDataURL(file)
    }
  }

  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        const result = reader.result as string
        // Remove data URL prefix to get just the base64 string
        const base64 = result.split(',')[1]
        resolve(base64)
      }
      reader.onerror = reject
      reader.readAsDataURL(file)
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!bookFile || !imageFile) {
      onError?.('Both book file and cover image are required')
      return
    }

    if (!formData.title.trim()) {
      onError?.('Title is required')
      return
    }

    if (formData.authors.some(author => !author.trim())) {
      onError?.('All author fields must be filled')
      return
    }

    setIsUploading(true)

    try {
      // Convert files to base64
      const bookBase64 = await convertFileToBase64(bookFile.file)
      const imageBase64 = await convertFileToBase64(imageFile.file)

      // Upload book file first
      const bookResponse = await fetch(`${BOOK_MANAGEMENT_API_BASE}/api/upload-book`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          metadata: {
            title: formData.title,
            slug: formData.slug || generateSlug(formData.title)
          },
          files: {
            book: {
              filename: bookFile.file.name,
              data: bookBase64
            }
          }
        })
      })

      if (!bookResponse.ok) {
        const error = await bookResponse.json()
        throw new Error(error.error || 'Failed to upload book')
      }

      const bookResult = await bookResponse.json()
      const finalSlug = bookResult.slug

      // Upload image with the final slug
      const imageResponse = await fetch(`${BOOK_MANAGEMENT_API_BASE}/api/upload-image`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          metadata: {
            slug: finalSlug
          },
          files: {
            image: {
              filename: imageFile.file.name,
              data: imageBase64
            }
          }
        })
      })

      if (!imageResponse.ok) {
        const error = await imageResponse.json()
        throw new Error(error.error || 'Failed to upload image')
      }

      const imageResult = await imageResponse.json()

      // Add book to library
      const addBookResponse = await fetch(`${BOOK_MANAGEMENT_API_BASE}/api/add-book`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          slug: finalSlug,
          title: formData.title,
          authors: formData.authors.filter(a => a.trim()),
          year: formData.year,
          license: formData.license,
          type: formData.type,
          coverUrl: imageResult.imageUrl,
          fileSize: bookResult.fileSize,
          fileType: 'PDF',
          description: formData.description,
          featured: formData.featured || false
        })
      })

      if (!addBookResponse.ok) {
        const error = await addBookResponse.json()
        throw new Error(error.error || 'Failed to add book to library')
      }

      const libraryResult = await addBookResponse.json()

      onSuccess?.(libraryResult)

      // Reset form
      setFormData({
        title: '',
        authors: [''],
        year: new Date().getFullYear(),
        license: 'CC-BY',
        type: 'book',
        description: '',
        slug: ''
      })
      setBookFile(null)
      setImageFile(null)

    } catch (error) {
      onError?.(error instanceof Error ? error.message : 'Upload failed')
    } finally {
      setIsUploading(false)
    }
  }

  return (
    <div className={`p-6 rounded-lg border ${
      isDark 
        ? 'bg-gray-800 border-gray-700' 
        : 'bg-white border-gray-200'
    }`}>
      <h2 className={`text-2xl font-bold mb-6 ${
        isDark ? 'text-white' : 'text-gray-900'
      }`}>
        Upload New Book
      </h2>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* File Upload Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Book File Upload */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${
              isDark ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Book File (PDF) *
            </label>
            <div className={`border-2 border-dashed rounded-lg p-4 text-center ${
              isDark 
                ? 'border-gray-600 hover:border-gray-500' 
                : 'border-gray-300 hover:border-gray-400'
            }`}>
              {bookFile ? (
                <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  <p className="font-medium">{bookFile.file.name}</p>
                  <p>{(bookFile.file.size / 1024 / 1024).toFixed(2)} MB</p>
                  <button
                    type="button"
                    onClick={() => setBookFile(null)}
                    className="mt-2 text-red-500 hover:text-red-700"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <div>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={(e) => handleFileChange('book', e.target.files?.[0] || null)}
                    className="hidden"
                    id="book-upload"
                  />
                  <label
                    htmlFor="book-upload"
                    className="cursor-pointer text-blue-500 hover:text-blue-700"
                  >
                    Click to upload PDF
                  </label>
                </div>
              )}
            </div>
          </div>

          {/* Image File Upload */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${
              isDark ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Cover Image *
            </label>
            <div className={`border-2 border-dashed rounded-lg p-4 text-center ${
              isDark 
                ? 'border-gray-600 hover:border-gray-500' 
                : 'border-gray-300 hover:border-gray-400'
            }`}>
              {imageFile ? (
                <div className="space-y-2">
                  {imageFile.preview && (
                    <img
                      src={imageFile.preview}
                      alt="Preview"
                      className="mx-auto h-20 w-16 object-cover rounded"
                    />
                  )}
                  <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    {imageFile.file.name}
                  </p>
                  <button
                    type="button"
                    onClick={() => setImageFile(null)}
                    className="text-red-500 hover:text-red-700 text-sm"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileChange('image', e.target.files?.[0] || null)}
                    className="hidden"
                    id="image-upload"
                  />
                  <label
                    htmlFor="image-upload"
                    className="cursor-pointer text-blue-500 hover:text-blue-700"
                  >
                    Click to upload image
                  </label>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Book Metadata */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Title */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${
              isDark ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleTitleChange(e.target.value)}
              className={`w-full px-3 py-2 border rounded-md ${
                isDark 
                  ? 'bg-gray-700 border-gray-600 text-white' 
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
              required
            />
          </div>

          {/* Slug */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${
              isDark ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Slug (auto-generated, editable)
            </label>
            <input
              type="text"
              value={formData.slug}
              onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
              className={`w-full px-3 py-2 border rounded-md ${
                isDark 
                  ? 'bg-gray-700 border-gray-600 text-white' 
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
              placeholder="Auto-generated from title"
            />
          </div>
        </div>

        {/* Authors */}
        <div>
          <label className={`block text-sm font-medium mb-2 ${
            isDark ? 'text-gray-300' : 'text-gray-700'
          }`}>
            Authors *
          </label>
          {formData.authors.map((author, index) => (
            <div key={index} className="flex gap-2 mb-2">
              <input
                type="text"
                value={author}
                onChange={(e) => handleAuthorsChange(index, e.target.value)}
                className={`flex-1 px-3 py-2 border rounded-md ${
                  isDark 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
                placeholder={`Author ${index + 1}`}
                required
              />
              {formData.authors.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeAuthor(index)}
                  className="px-3 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
                >
                  Remove
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={addAuthor}
            className="text-blue-500 hover:text-blue-700 text-sm"
          >
            + Add another author
          </button>
        </div>

        {/* Year, License, Type */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className={`block text-sm font-medium mb-2 ${
              isDark ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Year *
            </label>
            <input
              type="number"
              value={formData.year}
              onChange={(e) => setFormData(prev => ({ ...prev, year: parseInt(e.target.value) }))}
              className={`w-full px-3 py-2 border rounded-md ${
                isDark 
                  ? 'bg-gray-700 border-gray-600 text-white' 
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
              min="1000"
              max="2100"
              required
            />
          </div>

          <div>
            <label className={`block text-sm font-medium mb-2 ${
              isDark ? 'text-gray-300' : 'text-gray-700'
            }`}>
              License
            </label>
            <select
              value={formData.license}
              onChange={(e) => setFormData(prev => ({ ...prev, license: e.target.value }))}
              className={`w-full px-3 py-2 border rounded-md ${
                isDark 
                  ? 'bg-gray-700 border-gray-600 text-white' 
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
            >
              <option value="CC-BY">CC-BY</option>
              <option value="CC-BY-SA">CC-BY-SA</option>
              <option value="CC-BY-NC">CC-BY-NC</option>
              <option value="Public Domain">Public Domain</option>
            </select>
          </div>

          <div>
            <label className={`block text-sm font-medium mb-2 ${
              isDark ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Type
            </label>
            <select
              value={formData.type}
              onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as 'book' | 'paper' }))}
              className={`w-full px-3 py-2 border rounded-md ${
                isDark 
                  ? 'bg-gray-700 border-gray-600 text-white' 
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
            >
              <option value="book">Book</option>
              <option value="paper">Paper</option>
            </select>
          </div>
        </div>

        {/* Description */}
        <div>
          <label className={`block text-sm font-medium mb-2 ${
            isDark ? 'text-gray-300' : 'text-gray-700'
          }`}>
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            rows={4}
            className={`w-full px-3 py-2 border rounded-md ${
              isDark 
                ? 'bg-gray-700 border-gray-600 text-white' 
                : 'bg-white border-gray-300 text-gray-900'
            }`}
            placeholder="Brief description of the book/paper..."
          />
        </div>

        {/* Featured Toggle */}
        <div className="flex items-center">
          <input
            type="checkbox"
            id="featured"
            checked={formData.featured || false}
            onChange={(e) => setFormData(prev => ({ ...prev, featured: e.target.checked }))}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label htmlFor="featured" className={`ml-2 text-sm font-medium ${
            isDark ? 'text-gray-300' : 'text-gray-700'
          }`}>
            ⭐ Mark as Featured (will appear on homepage)
          </label>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isUploading}
            className={`px-6 py-2 rounded-md font-medium ${
              isUploading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-blue-500 hover:bg-blue-600'
            } text-white`}
          >
            {isUploading ? 'Uploading...' : 'Upload Book'}
          </button>
        </div>
      </form>
    </div>
  )
}